import { InfoLanguage, NimbuxHotelCheckOption } from "@bandido/shared/interfaces";
import { Box, Typography, FormControl, Select } from "@mui/material";

import { useTranslation } from "react-i18next";

import { FieldWrapper } from "../common";
import { MultiLanguageInstructionsEditor } from "./MultiLanguageInstructionsEditor";

export const CheckTimeSection = ({
  title,
  ageLabel,
  instructionsLabel,
  instructionsPlaceholder,
  data,
  onUpdateTime,
  onUpdateAge,
  onUpdateInstructions,
  onGenerateWithAI,
  isGeneratingTranslations,
  timeOptions,
  ageOptions
}: {
  type: 'checkin' | 'checkout';
  title: string;
  ageLabel: string;
  instructionsLabel: string;
  instructionsPlaceholder: string;
  data: NimbuxHotelCheckOption;
  onUpdateTime: (field: 'from' | 'to', value: string) => void;
  onUpdateAge: (value: string) => void;
  onUpdateInstructions: (language: InfoLanguage, data: string) => void;
  onGenerateWithAI?: () => void;
  isGeneratingTranslations: boolean;
  timeOptions: string[];
  ageOptions: string[];
}) => {
  const { t } = useTranslation();

  return (
    <Box display="flex" flexDirection="column" gap={5}>
      <Typography variant="subtitle2">
        {title}
      </Typography>
      <Box display="flex" gap={4}>
        <FieldWrapper label={t('hotelContent.view.checkinCheckout.from')}>
          <FormControl size="small" sx={{ minWidth: 120 }}>
            <Select
              native
              size="small"
              value={data.startTime}
              onChange={(e) => onUpdateTime('from', e.target.value)}
              displayEmpty
            >
              <option value="">{t('hotelContent.view.checkinCheckout.selectTime')}</option>
              {timeOptions.map((time) => (
                <option key={time} value={time}>
                  {time}
                </option>
              ))}
            </Select>
          </FormControl>
        </FieldWrapper>

        <FieldWrapper label={t('hotelContent.view.checkinCheckout.to')}>
          <FormControl size="small" sx={{ minWidth: 120 }}>
            <Select
              native
              size="small"
              value={data.endTime}
              onChange={(e) => onUpdateTime('to', e.target.value)}
              displayEmpty
            >
              <option value="">{t('hotelContent.view.checkinCheckout.selectTime')}</option>
              {timeOptions.map((time) => (
                <option key={time} value={time}>
                  {time}
                </option>
              ))}
            </Select>
          </FormControl>
        </FieldWrapper>

        <FieldWrapper label={ageLabel}>
          <FormControl size="small" sx={{ minWidth: 120 }}>
            <Select
              native
              size="small"
              value={data.minAge}
              onChange={(e) => onUpdateAge(e.target.value.toString())}
              displayEmpty
            >
              <option value="">{t('hotelContent.view.checkinCheckout.selectAge')}</option>
              {ageOptions.map((age) => (
                <option key={age} value={age}>
                  {age} {t('hotelContent.view.checkinCheckout.years')}
                </option>
              ))}
            </Select>
          </FormControl>
        </FieldWrapper>
      </Box>

      <Box display="flex" flexDirection="column" gap={2}>
        <Typography variant="subtitle2">
          {instructionsLabel}
        </Typography>
        <MultiLanguageInstructionsEditor
          values={data.instructions as Record<InfoLanguage, string>}
          onChange={onUpdateInstructions}
          placeholder={instructionsPlaceholder}
          onGenerateWithAI={onGenerateWithAI}
          isGeneratingTranslations={isGeneratingTranslations}
        />
      </Box>
    </Box>
  );
};
